import type { ReactNode } from 'react'
import type { EditorView } from 'prosemirror-view'
import type { Schema } from 'prosemirror-model'
import type { EditorState, Transaction } from 'prosemirror-state'
import { toggleMark } from 'prosemirror-commands'
import { ArtifactEditorFormatType } from '@/analytics/enums/artifact-editor.enums'
import { Bold, Italic, List, OrderedList, SyncProgress, Success } from '@/assets/icons'
import { toggleList } from '../prosemirror/toggle-list'
import { EditorSaveStatusType, FormatProperty, type EditorFormatState, type TextSizeLabel } from '../types'
import TextSizeDropdown from './TextSizeDropdown'
import './text-size-dropdown.css'

enum ButtonTitle {
  BOLD = 'Bold',
  ITALIC = 'Italic',
  BULLET_LIST = 'Bullet List',
  NUMBERED_LIST = 'Numbered List',
}

interface MarkdownEditorToolbarProps {
  editorView: EditorView | null
  schema: Schema
  saveStatus?: EditorSaveStatusType
  onRetry?: () => void
  currentTextStyle: TextSizeLabel
  formatState: EditorFormatState
  readOnly?: boolean
  /** Optional trailing toolbar slot, rendered next to the save status (e.g. the Add Template button). */
  templateButton?: ReactNode
  /** Observational: reports which formatting command ran. Never affects the command itself. */
  onFormatUsed?: (formatType: ArtifactEditorFormatType) => void
}

interface MenuButtonProps {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: ReactNode
}

function MenuButton({ onClick, active, disabled, title, children }: MenuButtonProps) {
  return (
    <div className="relative group">
      {/* Outer container - 28x28px */}
      <div className="w-7 h-7 flex items-center justify-center p-0 relative rounded-md">
        <button
          className={`w-full h-full flex items-center justify-center p-1 relative shrink-0 transition-colors ${
            active
              ? 'bg-brand-neutral-300 text-brand-neutral-950 rounded-md'
              : 'bg-transparent text-brand-neutral-950 rounded-full hover:bg-brand-neutral-300 hover:rounded-md'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={onClick}
          disabled={disabled}
          title={title}
          aria-label={title}
          aria-pressed={active}
          type="button"
        >
          <div className="w-4 h-4 flex items-center justify-center overflow-hidden relative shrink-0">{children}</div>
        </button>
      </div>

      {/* Tooltip */}
      <div className="absolute left-1/2 top-8 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-brand-neutral-500 text-white text-2xs font-medium px-1 py-0.5 rounded-xs whitespace-nowrap z-10 pointer-events-none">
        {title}
      </div>
    </div>
  )
}

function SaveStatus({ status, onRetry }: { status: EditorSaveStatusType; onRetry?: () => void }) {
  return (
    <div className="flex items-center gap-1 text-xs font-normal text-brand-neutral-600" role="status" aria-live="polite">
      {status === EditorSaveStatusType.DIRTY && <span>Unsaved</span>}
      {status === EditorSaveStatusType.SAVING && (
        <>
          <span>Saving</span>
          <SyncProgress className="w-3 h-3 text-brand-neutral-600" />
        </>
      )}
      {status === EditorSaveStatusType.SAVED && (
        <>
          <span>Saved</span>
          <Success className="w-3 h-3 text-brand-neutral-600" />
        </>
      )}
      {status === EditorSaveStatusType.ERROR && (
        <>
          <span>Auto-save failed. </span>
          <button type="button" onClick={onRetry} className="underline" aria-label="Saving failed, click to retry">
            Retry
          </button>
        </>
      )}
      {status === EditorSaveStatusType.CONFLICT && (
        <>
          <span>A newer version exists. </span>
          <button type="button" onClick={onRetry} className="underline" aria-label="Load the latest Matter artifact version">
            Load latest
          </button>
        </>
      )}
      {status === EditorSaveStatusType.CONNECTION_LOST && (
        <>
          <span>Connection lost</span>
        </>
      )}
    </div>
  )
}

export default function MarkdownEditorToolbar({
  editorView,
  schema,
  saveStatus = EditorSaveStatusType.IDLE,
  onRetry,
  currentTextStyle,
  formatState,
  readOnly = false,
  templateButton,
  onFormatUsed,
}: MarkdownEditorToolbarProps) {
  const handleCommand = (command: (state: EditorState, dispatch?: (tr: Transaction) => void) => boolean, formatType?: ArtifactEditorFormatType) => {
    if (!editorView) return
    command(editorView.state, editorView.dispatch)
    editorView.focus()
    if (formatType) onFormatUsed?.(formatType)
  }

  if (!editorView) return null

  return (
    <div className="bg-white relative w-full sticky top-0 z-10">
      {/* Border bottom */}
      <div className="absolute border-brand-neutral-200 border-b border-solid bottom-0 left-0 right-0 pointer-events-none" />

      {/* Main content */}
      <div className="relative w-full">
        <div className="box-border items-start justify-start py-2 relative w-full">
          {/* Buttons container - 28px tall, no padding */}
          <div className="flex flex-row justify-between box-border items-center px-2 h-7 [container-type:inline-size]">
            {/* Formatting controls retain the selected range after their command runs. */}
            {!readOnly ? (
              <div className="flex flex-row gap-1 items-center flex-none" data-preserve-editor-selection>
                {/* Text size dropdown */}
                <div className="box-border flex flex-col items-start justify-start p-0 relative">
                  <TextSizeDropdown editorView={editorView} schema={schema} currentTextStyle={currentTextStyle} />
                </div>

                {/* Separator */}
                <div className="flex flex-row items-center self-stretch">
                  <div className="box-border flex flex-row gap-2.5 h-full items-center justify-center p-0 relative shrink-0 w-2">
                    <div className="flex h-0 items-center justify-center relative shrink-0 w-0">
                      <div className="flex-none rotate-90">
                        <div className="h-0 relative w-7">
                          <div className="absolute bottom-0 left-0 right-0 top-0 h-px bg-brand-neutral-200" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Format buttons */}
                <MenuButton
                  onClick={() => handleCommand(toggleMark(schema.marks.strong), ArtifactEditorFormatType.bold)}
                  active={formatState[FormatProperty.STRONG]}
                  title={ButtonTitle.BOLD}
                >
                  <Bold className="w-full h-full" />
                </MenuButton>

                <MenuButton
                  onClick={() => handleCommand(toggleMark(schema.marks.em), ArtifactEditorFormatType.italic)}
                  active={formatState[FormatProperty.EM]}
                  title={ButtonTitle.ITALIC}
                >
                  <Italic className="w-full h-full" />
                </MenuButton>

                {/* Separator */}
                <div className="flex flex-row items-center self-stretch">
                  <div className="box-border flex flex-row gap-2.5 h-full items-center justify-center p-0 relative shrink-0 w-2">
                    <div className="flex h-0 items-center justify-center relative shrink-0 w-0">
                      <div className="flex-none rotate-90">
                        <div className="h-0 relative w-7">
                          <div className="absolute bottom-0 left-0 right-0 top-0 h-px bg-brand-neutral-200" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* List buttons */}
                <MenuButton
                  onClick={() => handleCommand(toggleList(schema.nodes.bullet_list, schema.nodes.list_item), ArtifactEditorFormatType.bulletList)}
                  active={formatState[FormatProperty.BULLET_LIST]}
                  title={ButtonTitle.BULLET_LIST}
                >
                  <List className="w-full h-full" />
                </MenuButton>

                <MenuButton
                  onClick={() => handleCommand(toggleList(schema.nodes.ordered_list, schema.nodes.list_item), ArtifactEditorFormatType.orderedList)}
                  active={formatState[FormatProperty.ORDERED_LIST]}
                  title={ButtonTitle.NUMBERED_LIST}
                >
                  <OrderedList className="w-full h-full" />
                </MenuButton>
              </div>
            ) : null}

            {/* Right side - save status + trailing toolbar slot */}
            <div className={readOnly ? 'ml-auto flex items-center gap-3' : 'flex items-center gap-3'}>
              <SaveStatus status={saveStatus} onRetry={onRetry} />
              {templateButton}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
